import type { HumanName as FhirHumanName, Practitioner } from "@medplum/fhirtypes";
import { getTRPCClient } from "@repo/api";
import { type SetPractitionerGoogleWorkspaceEmailInput as ApiInput, practitionerIdSchema } from "@repo/api/schemas";
import { ROLE_EXTENSION_URL } from "@repo/fhir-systems/extensions";
import { GOOGLE_WORKSPACE_EMAIL_SYSTEM } from "@repo/fhir-systems/identifiers";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useCollection } from "../core/hooks";
import type { PractitionerRow } from "../fhir/types";
import type { Address, CodeableConcept, ContactPoint, HumanName, Identifier, Period } from "./shared/types";

/**
 * Mind-Forge practitioner role, sourced from the `practitioner-role` extension
 * (set during setup from the AccessPolicy assignment).
 */
type PractitionerRole = "admin" | "clinician" | "staff" | "assistant";

/**
 * Practitioner — normalized read-model.
 * `name` is the primary HumanName picked by data-store (use=official > usual > first).
 * `role` is flattened from the Mind-Forge `practitioner-role` extension.
 */
export interface PractitionerDto {
  practitionerId: ApiInput["id"];
  /** Infrastructure locator for FHIR Reference fields; never use as domain identity. */
  fhirLocator?: `Practitioner/${string}`;
  active: boolean;

  name: HumanName;
  otherNames?: HumanName[];

  gender?: "male" | "female" | "other" | "unknown";
  birthDate?: string;

  telecom?: ContactPoint[];
  address?: Address[];

  qualifications?: Array<{
    code?: CodeableConcept;
    issuer?: string;
    period?: Period;
  }>;

  identifiers?: Identifier[];

  /**
   * The clinician's Google Workspace login, sourced from the
   * `google-workspace-email` identifier system. Undefined when the
   * practitioner has no Google Workspace identity.
   */
  googleWorkspaceEmail?: string;

  role?: PractitionerRole;
}

const PRACTITIONER_COLLECTION = "practitioner";

const VALID_ROLES: ReadonlySet<PractitionerRole> = new Set(["admin", "clinician", "staff", "assistant"]);

function pickPrimaryName(names: FhirHumanName[] | undefined): { primary: HumanName; others?: HumanName[] } {
  if (!names?.length) return { primary: {} };
  const officialIdx = names.findIndex((n) => n.use === "official");
  const usualIdx = names.findIndex((n) => n.use === "usual");
  const primaryIdx = officialIdx !== -1 ? officialIdx : usualIdx !== -1 ? usualIdx : 0;
  const primary = normalizeName(names[primaryIdx]);
  const others = names.filter((_, i) => i !== primaryIdx).map(normalizeName);
  return others.length ? { primary, others } : { primary };
}

function normalizeName(n: FhirHumanName | undefined): HumanName {
  if (!n) return {};
  return {
    ...(n.use ? { use: n.use as HumanName["use"] } : {}),
    // A HumanName is allowed to carry only `text`, and practitioners created
    // outside the seed's invite often do. Dropping it left the DTO nameless,
    // which every given+family display renders as nothing at all.
    ...(n.text ? { text: n.text } : {}),
    ...(n.prefix?.length ? { prefix: [...n.prefix] } : {}),
    ...(n.given?.length ? { given: [...n.given] } : {}),
    ...(n.family ? { family: n.family } : {}),
    ...(n.suffix?.length ? { suffix: [...n.suffix] } : {}),
  };
}

function normalizeTelecom(telecom: Practitioner["telecom"]): ContactPoint[] | undefined {
  if (!telecom?.length) return undefined;
  return telecom.map((t) => ({
    ...(t.system ? { system: t.system as ContactPoint["system"] } : {}),
    ...(t.value ? { value: t.value } : {}),
    ...(t.use ? { use: t.use as ContactPoint["use"] } : {}),
    ...(t.rank !== undefined ? { rank: t.rank } : {}),
  }));
}

function normalizeAddress(address: Practitioner["address"]): Address[] | undefined {
  if (!address?.length) return undefined;
  return address.map((a) => ({
    ...(a.use ? { use: a.use as Address["use"] } : {}),
    ...(a.type ? { type: a.type as Address["type"] } : {}),
    ...(a.line?.length ? { line: [...a.line] } : {}),
    ...(a.city ? { city: a.city } : {}),
    ...(a.state ? { state: a.state } : {}),
    ...(a.postalCode ? { postalCode: a.postalCode } : {}),
    ...(a.country ? { country: a.country } : {}),
  }));
}

function normalizeIdentifiers(identifier: Practitioner["identifier"]): Identifier[] | undefined {
  if (!identifier?.length) return undefined;
  return identifier.map((i) => ({
    ...(i.system ? { system: i.system } : {}),
    ...(i.value ? { value: i.value } : {}),
    ...(i.use ? { use: i.use as Identifier["use"] } : {}),
  }));
}

function flatCodeable(
  c: { coding?: Array<{ system?: string; code?: string; display?: string }>; text?: string } | undefined,
): CodeableConcept | undefined {
  if (!c) return undefined;
  const first = c.coding?.[0];
  return {
    ...(first?.system ? { system: first.system } : {}),
    ...(first?.code ? { code: first.code } : {}),
    ...(first?.display ? { display: first.display } : {}),
    ...(c.text ? { text: c.text } : {}),
  };
}

function normalizeQualifications(q: Practitioner["qualification"]) {
  if (!q?.length) return undefined;
  return q.map((entry) => {
    const code = flatCodeable(entry.code);
    const period: Period | undefined = entry.period
      ? {
          ...(entry.period.start ? { start: entry.period.start } : {}),
          ...(entry.period.end ? { end: entry.period.end } : {}),
        }
      : undefined;
    return {
      ...(code ? { code } : {}),
      ...(entry.issuer?.reference ? { issuer: entry.issuer.reference } : {}),
      ...(period ? { period } : {}),
    };
  });
}

function extractRole(extensions: Practitioner["extension"]): PractitionerRole | undefined {
  const value = extensions?.find((e) => e.url === ROLE_EXTENSION_URL)?.valueCode;
  return value && VALID_ROLES.has(value as PractitionerRole) ? (value as PractitionerRole) : undefined;
}

function extractGoogleWorkspaceEmail(identifier: Practitioner["identifier"]): string | undefined {
  return identifier?.find((i) => i.system === GOOGLE_WORKSPACE_EMAIL_SYSTEM)?.value;
}

function toDto(p: PractitionerRow): PractitionerDto {
  const { primary, others } = pickPrimaryName(p.name);
  const role = extractRole(p.extension);
  const googleWorkspaceEmail = extractGoogleWorkspaceEmail(p.identifier);

  return {
    practitionerId: practitionerIdSchema.parse(p.domainId),
    ...(p.id ? { fhirLocator: `Practitioner/${p.id}` as const } : {}),
    active: p.active ?? true,
    name: primary,
    ...(others ? { otherNames: others } : {}),
    ...(p.gender ? { gender: p.gender as PractitionerDto["gender"] } : {}),
    ...(p.birthDate ? { birthDate: p.birthDate } : {}),
    ...(normalizeTelecom(p.telecom) ? { telecom: normalizeTelecom(p.telecom) } : {}),
    ...(normalizeAddress(p.address) ? { address: normalizeAddress(p.address) } : {}),
    ...(normalizeQualifications(p.qualification) ? { qualifications: normalizeQualifications(p.qualification) } : {}),
    ...(normalizeIdentifiers(p.identifier) ? { identifiers: normalizeIdentifiers(p.identifier) } : {}),
    ...(googleWorkspaceEmail ? { googleWorkspaceEmail } : {}),
    ...(role ? { role } : {}),
  };
}

interface UsePractitionersResult {
  practitioners: PractitionerDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

interface UsePractitionersOptions {
  roles?: PractitionerRole[];
}

export function usePractitioners(options?: UsePractitionersOptions): UsePractitionersResult {
  const collection = useCollection(PRACTITIONER_COLLECTION);

  const result = useLiveQuery({
    queryKey: [collection.id, "practitioners"],
    query: (q) => q.from({ practitioner: collection }).fn.select(({ practitioner }) => toDto(practitioner)),
  });

  const all = (result.data as PractitionerDto[] | undefined) ?? [];
  const roles = options?.roles;
  const practitioners = roles ? all.filter((p) => p.role && roles.includes(p.role as PractitionerRole)) : all;

  return {
    practitioners,
    isLoading: result.isLoading,
    isReady: result.isReady,
    isError: result.isError,
  };
}

interface UsePractitionerResult {
  practitioner: PractitionerDto | null;
  isLoading: boolean;
  isReady: boolean;
}

export function usePractitioner(practitionerId: string | undefined): UsePractitionerResult {
  const collection = useCollection(PRACTITIONER_COLLECTION);

  const result = useLiveQuery({
    queryKey: [collection.id, "practitioner", practitionerId ?? null],
    query: (q) => {
      if (!practitionerId) return undefined;
      return q
        .from({ practitioner: collection })
        .where(({ practitioner }) => eq(practitioner.domainId, practitionerId))
        .fn.select(({ practitioner }) => toDto(practitioner))
        .findOne();
    },
  });

  return {
    practitioner: (result.data as PractitionerDto | undefined) ?? null,
    isLoading: result.isLoading,
    isReady: result.isReady,
  };
}

export interface SetPractitionerGoogleWorkspaceEmailInput {
  practitionerId: ApiInput["id"];
  googleWorkspaceEmail: string;
}

/**
 * Intent: "set this clinician's Google Workspace identity" (MIN-281) — the
 * identity domain-wide delegation impersonates to mint Meet spaces. Domain
 * refusals (outside our Workspace domain, empty, malformed) surface via the
 * tRPC error link.
 */
export async function setPractitionerGoogleWorkspaceEmail(
  input: SetPractitionerGoogleWorkspaceEmailInput,
): Promise<void> {
  await getTRPCClient().practitioner.setGoogleWorkspaceEmail.mutate({
    id: input.practitionerId,
    googleWorkspaceEmail: input.googleWorkspaceEmail,
  });
}
